import React, { useState } from "react";
import { PageHeader, PhxButton } from "@/design-system";
import { useOffline } from "@/offline/OfflineProvider";

/**
 * Create a sales invoice draft that works offline. The draft is queued and
 * created on the server when online; it gets its real number there. Submitting
 * and posting (stock, credit checks) always happen online afterwards.
 */
export default function NewInvoicePage() {
  const { cache, enqueue, online } = useOffline();
  const [partyId, setPartyId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [lines, setLines] = useState([]);
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [done, setDone] = useState(null);

  const addLine = () => {
    const p = cache.products.find((x) => x.id === productId);
    if (!p || !(Number(qty) > 0) || price === "") return;
    setLines([...lines, {
      product_id: p.id, product_name: p.name, sku: p.sku,
      qty: Number(qty), unit_price: Number(price), warehouse_id: warehouseId || undefined,
    }]);
    setProductId(""); setQty("1"); setPrice("");
  };

  const save = async () => {
    const item = await enqueue("create_document", {
      type_code: "SALES_INVOICE",
      document_date: new Date().toISOString().slice(0, 10),
      party_id: partyId,
      warehouse_id: warehouseId || undefined,
      lines,
    });
    setDone(item.provisionalNumber);
    setLines([]);
  };

  return (
    <div data-testid="new-invoice" className="space-y-3 text-sm">
      <PageHeader title="New sales invoice" />
      {!online && <p className="text-amber-700">Offline — the invoice will be saved on this device and synced later.</p>}
      {done && <p className="text-green-700" data-testid="invoice-queued">Saved as {done}. Open Sync to see its status.</p>}
      <label className="block">Customer
        <select aria-label="customer" className="block border p-1" value={partyId} onChange={(e) => setPartyId(e.target.value)}>
          <option value="">Select…</option>
          {cache.parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="block">Warehouse
        <select aria-label="warehouse" className="block border p-1" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
          <option value="">Select…</option>
          {cache.warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </label>
      <div className="flex gap-2 items-end">
        <select aria-label="product" className="border p-1" value={productId} onChange={(e) => setProductId(e.target.value)}>
          <option value="">Product…</option>
          {cache.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
        </select>
        <input aria-label="qty" type="number" min="0" className="w-20 border p-1" value={qty} onChange={(e) => setQty(e.target.value)} />
        <input aria-label="price" type="number" min="0" placeholder="Price" className="w-24 border p-1" value={price} onChange={(e) => setPrice(e.target.value)} />
        <PhxButton variant="secondary" onClick={addLine}>Add line</PhxButton>
      </div>
      <ul className="divide-y border bg-white">
        {lines.map((l, i) => <li key={i} className="p-2">{l.product_name} × {l.qty} @ {l.unit_price}</li>)}
      </ul>
      <PhxButton onClick={save} disabled={!partyId || !warehouseId || lines.length === 0}>Save invoice</PhxButton>
    </div>
  );
}
